import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import TimeService from "src/services/timeService/timeService";

import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ElasticService from "@services/elasticService";
import { ProgressBar } from "@shared/ProgressBar";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { TextLink } from "@shared/TextLink";

import { useStatusCardStyles } from "../StatusCard.styles";

const getSubscriptionStatus = (expirationDate: string | null, theme: Theme) => {
    const result: {
        caption: string;
        color: string;
    } = {
        caption: i18next.t("common:status.active"),
        color: theme.palette.emteria.success,
    };

    if (!expirationDate) {
        // use default "Active" status
        return result;
    }

    const dateOfExpiration = TimeService.getLocalDate(expirationDate, "ISO", "date");
    const daysUntilExpiration = TimeService.getDaysDifferenceUntilNow(expirationDate);

    if (daysUntilExpiration > 90) {
        // use default "Active" status
    } else if (daysUntilExpiration > 14 && daysUntilExpiration <= 90) {
        result.caption = i18next.t("pages:overviewPage.cards.subscriptionStatus.expiresOn", { date: dateOfExpiration });
        result.color = theme.palette.emteria.warning;
    } else if (daysUntilExpiration > 0 && daysUntilExpiration <= 14) {
        result.caption = i18next.t("pages:overviewPage.cards.subscriptionStatus.daysLeft", { days: daysUntilExpiration });
        result.color = theme.palette.emteria.error;
    } else if (daysUntilExpiration <= 0) {
        result.caption = i18next.t("pages:overviewPage.cards.subscriptionStatus.expiredOn", { date: dateOfExpiration });
        result.color = theme.palette.emteria.error;
    }

    return result;
};

const SubscriptionStatusCard = () => {
    const { t } = useTranslation(["pages"]);
    const { classes: statusCardClasses } = useStatusCardStyles();
    const navigate = useNavigate();
    const { data: subscription } = useCurrentSubscription({});
    const theme = useTheme();

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const text = e.currentTarget.textContent;
        if (text) {
            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "subscription-status",
                text,
            });
        }
        void navigate("/workspace/subscription");
    };

    if (!subscription) {
        return null;
    }

    const { caption, color } = getSubscriptionStatus(subscription.expirationDate, theme);
    return (
        <SectionPanel title={t("pages:overviewPage.cards.subscriptionStatus.title")}>
            <TextLink onClick={handleOnClick} tooltip={t("pages:overviewPage.cards.subscriptionStatus.tooltip")}>
                {caption}
            </TextLink>
            <ProgressBar className={statusCardClasses.progressBar} progress={100} color={color} hideLabel={true} />
        </SectionPanel>
    );
};

export default SubscriptionStatusCard;
